/**
 * アカウント登録画面
 */
import { css } from "@emotion/react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  InputAdornment,
  Theme,
  useTheme,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { FirebaseError } from "firebase/app";
import { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  ChangeEvent,
  FormEvent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  AccountForm,
  AccountFormTextField,
  accountFormLinkStyle,
} from "~/components/account/AccountForm";
import ExamSignupHeader from "~/components/account/ExamSignupHeader";
import PageHead from "~/components/common/PageHead";
import {
  loginWithFirebaseAuth,
  signupWithFirebaseAuth,
} from "~/lib/client/auth";
import { logEvent } from "~/lib/client/firebase";
import { useSignupExamContext } from "~/lib/client/hooks/useSignupExamContext";
import { Layout } from "~/lib/client/layout";
import { useActivityLogger } from "~/lib/client/logger/useActivityLogger";
import {
  EXAM_SIGNUP_SUBMIT_LABEL,
  authDestination,
  buildAccountHref,
  examDestination,
  examSignupDestinationCopy,
  examSignupFreeAccountCopy,
  safeInternalRedirect,
} from "~/lib/client/signupExamContext";
import { constants } from "~/lib/constants";
import { createGraphqlClient } from "~/lib/graphql/graphqlClient";
import { useAppContext } from "~/pages/_app";

const SignUpPage: NextPage = () => {
  const pageName = "signUp";
  const router = useRouter();
  const theme = useTheme();
  const sendActivityLogger = useActivityLogger();
  const { currentUser, setCurrentUser } = useAppContext();
  const examContextState = useSignupExamContext(
    router.query.exam_slug,
    router.isReady
  );
  const examContext = examContextState.context;

  const [user, setUser] = useState<{
    name: string | null;
    email: string | null;
    password: string | null;
  }>({
    name: null,
    email: null,
    password: null,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const trackedViewSlug = useRef<string | null>(null);
  const trackedContextError = useRef(false);
  const trackedMissingContext = useRef(false);
  const trackedInvalidContext = useRef(false);

  const loginHref = useMemo(
    () =>
      buildAccountHref("/account/login", {
        examSlug: examContext?.examSlug ?? examContextState.examSlug,
        redirect: safeInternalRedirect(router.query.redirect),
      }),
    [examContext?.examSlug, examContextState.examSlug, router.query.redirect]
  );

  const trackExamEvent = useCallback(
    (eventName: string, extra?: Record<string, string>) => {
      const examSlug = examContext?.examSlug ?? examContextState.examSlug;
      if (!examSlug) return;

      try {
        logEvent(eventName, { exam_slug: examSlug, ...extra });
      } catch (error) {
        console.error("Failed to send exam signup event", error);
      }
    },
    [examContext?.examSlug, examContextState.examSlug]
  );

  const handleSubmitOnClick = useCallback(async () => {
    setIsSubmitting(true);
    setErrorMessage("");

    if (!user.name || !user.email || !user.password) {
      setIsSubmitting(false);
      setErrorMessage("不足している入力項目があります。");
      return;
    }

    trackExamEvent("exam_signup_submit");

    try {
      await signupWithFirebaseAuth(user.name, user.email, user.password);
      const credential = await loginWithFirebaseAuth(user.email, user.password);
      const idToken = await credential.user.getIdToken();
      const client = createGraphqlClient(idToken);
      const { me } = await client.me();

      if (!me) {
        setIsSubmitting(false);
        await router.push("/");
        return;
      }

      setCurrentUser({
        id: me.id,
        name: me.name,
        iconUrl: me.avatarImageUrl,
      });
      trackExamEvent("exam_signup_complete");
      await router.push(
        authDestination(examContext, router.query.redirect, "/")
      );
    } catch (error) {
      const errorCode =
        error instanceof FirebaseError ? error.code : "unexpected_error";
      trackExamEvent("exam_signup_error", { error_code: errorCode });

      if (error instanceof FirebaseError) {
        switch (error.code) {
          case "auth/email-already-exists":
          case "auth/email-already-in-use":
            setErrorMessage("そのメールアドレスはすでに利用されています。");
            break;
          case "auth/invalid-email":
            setErrorMessage("登録されたメールアドレスは無効です。");
            break;
          case "auth/wrong-password":
            setErrorMessage("パスワードが間違えています");
            break;
          case "auth/too-many-requests":
            setErrorMessage(
              "ログインの失敗回数が上限に達しました。ログインするにはしばらくお待ち下さい。"
            );
            break;
          case "auth/user-not-found":
            setErrorMessage("そのユーザは見つかりませんでした。");
            break;
          case "auth/weak-password":
            setErrorMessage("より強固なパスワードを設定してください。");
            break;
          default:
            setErrorMessage(
              `${error.code} 次のリンクより管理者にご連絡ください。 ${constants.contactForm.url}`
            );
            break;
        }
      } else {
        setErrorMessage(
          `予期せぬエラーが発生しました。次のリンクより管理者にご連絡ください。 ${constants.contactForm.url}`
        );
      }
      setIsSubmitting(false);
    }
  }, [
    examContext,
    router,
    setCurrentUser,
    trackExamEvent,
    user.email,
    user.name,
    user.password,
  ]);

  const handleSubmit = useCallback(
    async (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      await handleSubmitOnClick();
    },
    [handleSubmitOnClick]
  );

  useEffect(() => {
    if (!examContext || trackedViewSlug.current === examContext.examSlug)
      return;
    trackedViewSlug.current = examContext.examSlug;
    trackExamEvent("exam_signup_view");
  }, [examContext, trackExamEvent]);

  useEffect(() => {
    if (!examContextState.isError || trackedContextError.current) return;
    trackedContextError.current = true;
    trackExamEvent("exam_signup_context_error");
  }, [examContextState.isError, trackExamEvent]);

  useEffect(() => {
    if (
      !examContextState.requested ||
      examContextState.isLoading ||
      examContextState.isError ||
      examContext ||
      trackedMissingContext.current
    ) {
      return;
    }

    if (examContextState.examSlug) {
      trackedMissingContext.current = true;
      trackExamEvent("exam_signup_context_missing");
      return;
    }

    if (trackedInvalidContext.current) return;
    trackedInvalidContext.current = true;
    try {
      logEvent("exam_signup_context_invalid");
    } catch (error) {
      console.error("Failed to send exam signup event", error);
    }
  }, [
    examContext,
    examContextState.examSlug,
    examContextState.isError,
    examContextState.isLoading,
    examContextState.requested,
    trackExamEvent,
  ]);

  useEffect(() => {
    if (!examContext || !currentUser || isSubmitting) return;
    void router.replace(examDestination(examContext));
  }, [currentUser, examContext, isSubmitting, router]);

  useEffect(() => {
    if (sendActivityLogger === undefined) return;

    void sendActivityLogger({
      actionName: "show",
      pageName,
    });
  }, [sendActivityLogger]);

  const isLoading =
    !router.isReady ||
    examContextState.isLoading ||
    (!!examContext && currentUser === undefined) ||
    (!!examContext && !!currentUser && !isSubmitting);

  if (isLoading) {
    return (
      <>
        <PageHead title="アカウント登録 | FUSSY" />
        <Layout layoutType="YTLayout">
          <main css={loadingStyle(theme)} aria-busy="true">
            <CircularProgress aria-label="読み込み中" />
          </main>
        </Layout>
      </>
    );
  }

  const submitLabel = examContext
    ? EXAM_SIGNUP_SUBMIT_LABEL
    : "アカウントを作成する";

  return (
    <>
      <PageHead
        title={
          examContext
            ? `${examContext.examName}の受験アカウント登録 | FUSSY`
            : undefined
        }
      />
      <Layout layoutType="YTLayout">
        <main css={rootStyle(theme)}>
          <AccountForm
            title={
              examContext ? (
                <ExamSignupHeader context={examContext} />
              ) : (
                <h2 css={headingStyle}>アカウントを作成する</h2>
              )
            }
            maxWidth={examContext ? 480 : 360}
            onSubmit={handleSubmit}
            error={{ errorMessage, setErrorMessage }}
          >
            {examContext && (
              <p css={freeAccountCopyStyle}>
                {examSignupFreeAccountCopy(examContext)}
              </p>
            )}

            <AccountFormTextField
              id="input-username"
              label="User Name"
              type="text"
              required
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUser((current) => ({
                  ...current,
                  name: event.target.value,
                }))
              }
            />

            <AccountFormTextField
              id="input-email"
              label="Email"
              type="email"
              required
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUser((current) => ({
                  ...current,
                  email: event.target.value,
                }))
              }
            />

            <AccountFormTextField
              id="input-password"
              label="Password"
              required
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                setUser((current) => ({
                  ...current,
                  password: event.target.value,
                }))
              }
              type={showPassword ? "text" : "password"}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((show) => !show)}
                      onMouseDown={(event: MouseEvent<HTMLButtonElement>) =>
                        event.preventDefault()
                      }
                      sx={{ color: "#282828" }}
                      aria-label={
                        showPassword ? "パスワードを非表示" : "パスワードを表示"
                      }
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <p css={textStyle}>
              「{submitLabel}」をクリックすることで、当社の
              <Link
                href="https://www.notion.so/yooya19space/12d53f4ffddb456eac3bcf87d4108458"
                css={accountFormLinkStyle}
              >
                利用規約
              </Link>
              および
              <Link
                href="https://www.notion.so/yooya19space/17ddfc821bce4a66b55050723257ace3"
                css={accountFormLinkStyle}
              >
                プライバシーポリシー
              </Link>
              に同意したものとみなします。
            </p>

            <Button
              type="submit"
              disabled={isSubmitting}
              css={gradationButtonStyle}
              variant="contained"
            >
              {isSubmitting ? "登録中..." : submitLabel}
            </Button>

            {examContext && (
              <p css={destinationCopyStyle}>
                {examSignupDestinationCopy(examContext)}
              </p>
            )}

            <p css={loginLinkTextStyle}>
              または
              <Link css={accountFormLinkStyle} href={loginHref}>
                ログインする
              </Link>
            </p>
          </AccountForm>
        </main>
      </Layout>
    </>
  );
};

const headingStyle = css`
  margin: 0 0 2rem;
`;

const rootStyle = (theme: Theme) => css`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 90vh;
  padding: 2rem 0;
  background: ${theme.palette.background.default};
`;

const loadingStyle = (theme: Theme) => css`
  display: grid;
  place-items: center;
  min-height: 90vh;
  background: ${theme.palette.background.default};
`;

const textStyle = css`
  width: 100%;
  font-size: 0.75rem;
  line-height: 1.6;
`;

const loginLinkTextStyle = css`
  width: 100%;
  font-size: 0.75rem;
  line-height: 1.6;
  text-align: center;
`;

const freeAccountCopyStyle = css`
  width: 100%;
  margin: 0 0 0.75rem;
  padding: 0.75rem;
  border-left: 3px solid #ff480f;
  background: #fff7f3;
  color: #333;
  font-size: 0.82rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
`;

const gradationButtonStyle = css`
  width: 100%;
  min-height: 44px;
  margin-top: 1.25rem;
`;

const destinationCopyStyle = css`
  width: 100%;
  margin: 0.75rem 0 0;
  color: #555;
  font-size: 0.78rem;
  line-height: 1.6;
  text-align: center;
  overflow-wrap: anywhere;
`;

export default SignUpPage;
